import { GrowthBook } from '@growthbook/growthbook-react';
import { getEnvironment, ENVIRONMENT_PRODUCTION, isEndToEndTest } from 'utils/environment';
import configJSON from 'config.json';

const GROWTHBOOK_INIT_TIMEOUT = 2000;

/**
 * Generates a GrowthBook payload for server-side rendering (SSR) hydration.
 *
 * This function creates a temporary GrowthBook instance on the server, initializes it
 * with feature definitions from the GrowthBook API, and returns a payload that can be
 * used to hydrate the client-side GrowthBook instance. This eliminates client-side
 * flickering and ensures consistent feature flag behavior between server and client.
 *
 * The payload contains all feature definitions and experiment configurations needed
 * for client-side evaluation. User attributes are not set here - they are handled
 * on the client-side when the GrowthBook instance is initialized.
 *
 * @returns A payload object containing feature definitions
 *
 * @example
 * ```typescript
 * // In the getInitialProps function
 * ZoocasaNextApp.getInitialProps = async (context: AppContext): Promise<ZoocasaNextInitialProps> => {
 *   const growthBookPayload = await getGrowthBookPayload();
 *
 *   return {
 *     ...await App.getInitialProps(context),
 *     growthBookPayload,
 *     // ... other props
 *   };
 * }
 * ```
 *
 * @example
 * ```typescript
 * // In a custom server-side handler
 * const handler: HandlerType<MyPageProps> = async (context, features, tenant) => {
 *   const growthBookPayload = await getGrowthBookPayload();
 *
 *   return {
 *     props: {
 *       growthBookPayload,
 *       // ... other props
 *   };
 * }
 * ```
 *
 * @example
 * ```typescript
 * // In a custom server-side handler
 * const handler: HandlerType<MyPageProps> = async (context, features, tenant) => {
 *   const growthBookPayload = await getGrowthBookPayload();
 *
 *   return {
 *     props: {
 *       growthBookPayload,
 *       // ... other props
 *     }
 *   };
 * };
 * ```
 * @see {@link https://docs.growthbook.io/lib/react#server-side-rendering-ssr} - GrowthBook React SSR documentation
 */
export async function getGrowthBookPayload() {
  // Create and initialize a GrowthBook instance
  const gb = new GrowthBook({
    apiHost: configJSON.growthbook.apiHost,
    clientKey: configJSON.growthbook.clientKey,
    enableDevMode: getEnvironment() !== ENVIRONMENT_PRODUCTION,
    enabled: !isEndToEndTest,
  });
  await gb.init({ timeout: GROWTHBOOK_INIT_TIMEOUT });

  // Get the payload to hydrate the client-side GrowthBook instance
  // Note: Attributes are set on the client-side, not server-side
  const payload = gb.getPayload();

  // Cleanup
  gb.destroy();

  // Pass the result into your component
  return payload;
}